// group-trust-core.js
// The actual trust/authentication logic — split out from group-trust.js
// specifically so it (and chat.js/group.js/group-chat.js, which don't
// depend on this at all but live in the same "browser-bundleable core"
// category) can be bundled unchanged for a browser target. This is the
// security-critical part: MLS's AuthenticationService is intentionally
// left to the application (RFC 9750) — MLS handles group key agreement,
// but "should I trust this identity" is our call. We answer it the same
// way chat.js does for 1:1 sessions: trust-on-first-use (TOFU), pin the
// key on first contact, flag mismatches on every contact after that.
//
// The credential's `identity` bytes are the peer's long-term Ed25519
// public key (identity-core.js) — the same identity used for 1:1 chat
// and printed as a fingerprint for out-of-band verification. So a
// contact you've already verified for 1:1 chat is automatically trusted
// in group contexts too, and vice versa: this is the SINGLE trust store
// for both 1:1 chat (a 2-member MLS group, see chat.js) and multi-party
// group chat.
//
// group-trust.js (Node) and src/browser/group-trust-browser.js both
// wrap this exact class with their own storage backend — this logic
// itself is never duplicated across platforms.

import { fingerprintOf } from './identity-core.js'
import { publicKeyFromRaw } from '@libp2p/crypto/keys'

export class GroupTrustStore {
  /** @param {{load: () => Promise<object|null>, save: (data: object) => Promise<void>}} storage */
  constructor (storage) {
    this.storage = storage
    this.data = {} // hex(identityBytes) -> { signaturePubB64, fingerprint, firstSeen }
  }

  async load () {
    this.data = (await this.storage.load()) ?? {}
  }

  async _persist () {
    await this.storage.save(this.data)
  }

  /**
   * Build an AuthenticationService for ts-mls backed by this store.
   * @param {(identityHex: string, fingerprint: string) => void} onNewIdentity -
   *   called on first contact so the caller can surface the fingerprint for
   *   out-of-band verification (same UX as 1:1 chat's "new contact" prompt).
   */
  toAuthService (onNewIdentity) {
    return {
      validateCredential: async (credential, signaturePublicKey) => {
        const identityBytes = credential.identity
        const identityHex = Buffer.from(identityBytes).toString('hex')
        const sigPubB64 = Buffer.from(signaturePublicKey).toString('base64')

        // Structural check: group.js always mints key packages using the
        // peer's persistent identity key AS the MLS signing key (see
        // generateOwnKeyPackage), so identity bytes and signature key must
        // be identical by construction. If they're not, this credential
        // doesn't actually prove control of the claimed identity — reject
        // outright rather than TOFU-pinning a key that isn't provably tied
        // to it.
        if (Buffer.from(identityBytes).toString('base64') !== sigPubB64) {
          return false
        }

        const existing = this.data[identityHex]
        if (existing) return true // same identity, and we just verified the signing key matches it

        // First contact: pin it. Compute a fingerprint the same way
        // identity-core.js does, so it's comparable to a 1:1 chat fingerprint.
        let fingerprint = identityHex
        try {
          const pub = publicKeyFromRaw(identityBytes)
          fingerprint = fingerprintOf(pub)
        } catch {
          // identity bytes weren't a raw Ed25519 key (e.g. a test/basic
          // identity) — fall back to a hex dump, still useful to compare.
        }

        this.data[identityHex] = {
          signaturePubB64: sigPubB64,
          fingerprint,
          firstSeen: new Date().toISOString()
        }
        await this._persist()
        if (onNewIdentity) onNewIdentity(identityHex, fingerprint)
        return true
      }
    }
  }
}
